import { CartItemList } from '@/models/cart/cart-item'
import _ from 'lodash'

/**
 * 販促目的で使用される、会員価格で購入した場合のカート内合計金額などの、販促用データを算出する。
 * データをどのように表示するかは利用側の責務。
 * 仕様については @see https://github.com/my-color/front/issues/6041#issuecomment-1031135130
 * 仕様が変更された場合は、ポリシーの内容を差し替えること。
 *
 */
export class MembershipPlanPromotionPolicy {
  constructor(
    private readonly feature: MembershipPlanFeature,
    private readonly user: User,
    private readonly cartItemList: CartItemList
  ) {}

  /**
   * 販促情報を返す。「販促しない」という場合も、明示的な型で返す。
   *
   * 販促情報としてはとくに、カート内商品に対応して、もし有料会員だった場合の価格を返す。
   * そのため、カート内商品が空であれば販促内容は存在せず、（0円の販促としてではなく）「販促しない」として返す。
   */
  get promotion(): MembershipPlanPromotion | NoMembershipPlanPromotion {
    if (!this.userIsPromotionTarget) {
      return noPromotion
    }

    if (this.cartItemList.list.length === 0) {
      return noPromotion
    }

    return {
      shouldPromote: true,
      totalMembershipPrice: this.totalMembershipPrice,
    }
  }

  /**
   * ユーザ自身の属性として販促対象かを判定する。
   * ユーザが持つカート内容などに応じた判定は責務外。
   */
  private get userIsPromotionTarget(): boolean {
    if (!this.feature.isEnabled) return false

    return !this.user.isPaidMember
  }

  private get totalMembershipPrice(): number {
    return _.sumBy(this.cartItemList.list, (item) => {
      const validPrice = item.currentValidPrice

      if (!validPrice) {
        throw new Error(
          `Cart ${item.id} has no currentValidPrice field.`
        )
      }

      /**
       * 「非有料会員に対して、会員価格であれば合計いくら」という値を返したいというのが文字通りの仕様であるが、
       * 非有料会員であることが前提されているので、非有料会員であるために採択されなかった = unselectedな価格のみを走査している。
       *
       * ただしunselectedな価格がそもそも存在していない=selectedな価格（したがって通常価格）しか存在しない場合は、
       * selectedな価格を無視してしまうと「会員価格であれば合計いくら」というときの「合計」にそぐわなくなってしまうので、selectedな価格を採用する。
       */

      const minUnselectedPrice = _.min(
        validPrice.prices.unselected.map(priceCandidate => priceCandidate.price)
      )

      const promotionUnitPrice = minUnselectedPrice === undefined ?
        validPrice.prices.selected.price : minUnselectedPrice

      return promotionUnitPrice * item.unitCount
    })
  }
}

export interface MembershipPlanFeature {
  isEnabled: boolean
}

export interface User {
  isPaidMember: boolean
}

export interface MembershipPlanPromotion {
  readonly shouldPromote: true
  /**
   * 現在の仕様としては、会員価格で購入した場合の、各商品の最安価格の合計値を算出している。
   * また有料会員でない場合は、販促すべき内容がないため、合計値はundefinedで返される。
   */
  readonly totalMembershipPrice: number
}

export interface NoMembershipPlanPromotion {
  readonly shouldPromote: false
}
export const noPromotion: NoMembershipPlanPromotion = {
  shouldPromote: false,
}
